import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { formatDate } from '../utils/appDateTime';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { ArrowLeft, Bell, Loader2, Search, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { fetchOverdueDuesRows } from '../utils/fetchOverdueDues';
import { subscribeFeeDataChanged } from '../utils/feeDataEvents';
import { filterDuesBySearch, getApiErrorMessage, isNotFoundError } from '../utils/feeOverdueDues';
import api from '../utils/api';

const FeeAccountDues = () => {
  const navigate = useNavigate();
  const { user, canManageFeature } = useAuth();
  const canManageFees = canManageFeature('fees');
  const { toast } = useToast();

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 50, pages: 0, overdue_total: 0 });
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [error, setError] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);
  const [notifyUnavailable, setNotifyUnavailable] = useState(false);
  const searchTimer = useRef(null);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  const buildParams = useCallback(() => {
    const academicYearId = getSelectedAcademicYearId();
    return {
      page,
      limit: 50,
      ...(user?.school_id ? { school_id: user.school_id } : {}),
      ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    };
  }, [user?.school_id, page, debouncedSearch]);

  const fetchDues = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUsedFallback(false);

    if (!user?.school_id) {
      setError('School context required. Log in with a school staff account.');
      setRows([]);
      setMeta({ total: 0, page: 1, limit: 50, pages: 0, overdue_total: 0 });
      setLoading(false);
      return;
    }

    try {
      const { rows: data, meta: nextMeta, usedFallback: fallback } = await fetchOverdueDuesRows(buildParams());
      setRows(data);
      setMeta(nextMeta);
      setUsedFallback(fallback);
      setNotifyUnavailable(fallback);
    } catch (e) {
      console.error('Failed to load overdue dues', e);
      setRows([]);
      setMeta({ total: 0, page: 1, limit: 50, pages: 0, overdue_total: 0 });
      const status = e.response?.status;
      if (status === 403) {
        setError('You do not have permission to view fee dues.');
      } else if (status === 401) {
        setError('Session expired. Please log in again.');
      } else if (status === 400) {
        setError(getApiErrorMessage(e, 'School context required.'));
      } else {
        setError(getApiErrorMessage(e, 'Failed to load overdue fee dues.'));
      }
    } finally {
      setLoading(false);
    }
  }, [user?.school_id, buildParams]);

  useEffect(() => {
    fetchDues();
  }, [fetchDues]);

  useEffect(() => {
    const handler = () => fetchDues();
    window.addEventListener('academicYearChanged', handler);
    const unsubFee = subscribeFeeDataChanged((e) => {
      const sid = e.detail?.schoolId;
      if (!sid || sid === user?.school_id) fetchDues();
    });
    return () => {
      window.removeEventListener('academicYearChanged', handler);
      unsubFee();
    };
  }, [fetchDues, user?.school_id]);

  const displayRows = usedFallback ? filterDuesBySearch(rows, debouncedSearch) : rows;
  const overdueTotal = meta.overdue_total ?? 0;

  const formatCurrency = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const handleNotify = async () => {
    if (notifyUnavailable) {
      toast({
        title: 'Notify not available',
        description: 'Deploy the latest backend API to enable bulk overdue notifications.',
        variant: 'destructive',
      });
      return;
    }

    setNotifying(true);
    try {
      const params = {
        ...(user?.school_id ? { school_id: user.school_id } : {}),
        ...(getSelectedAcademicYearId() ? { academic_year_id: getSelectedAcademicYearId() } : {}),
      };
      let res;
      try {
        res = await api.post('/fees/parent-accounts/notify-overdue', null, { params });
      } catch (notifyErr) {
        if (isNotFoundError(notifyErr)) {
          setNotifyUnavailable(true);
          toast({
            title: 'Notify not available',
            description: 'Deploy the latest backend (notify-overdue endpoint) to enable bulk notifications.',
            variant: 'destructive',
          });
          return;
        }
        throw notifyErr;
      }

      const data = res.data.data || {};
      const sentEmail = data.sent_email ?? 0;
      const sentInApp = data.sent_in_app ?? 0;
      const sentWhatsapp = data.sent_whatsapp ?? 0;
      const skipped = data.skipped ?? 0;
      const failed = data.failed ?? 0;

      toast({
        title: 'Notifications sent',
        description: `Email: ${sentEmail}, In-app: ${sentInApp}, WhatsApp: ${sentWhatsapp}. Skipped: ${skipped}${failed > 0 ? `, Failed: ${failed}` : ''}.`,
        variant: failed > 0 ? 'destructive' : 'default',
      });

      if (data.errors?.length) {
        console.warn('Notify errors:', data.errors);
      }
    } catch (e) {
      toast({
        title: 'Notify failed',
        description: getApiErrorMessage(e, 'Could not send notifications'),
        variant: 'destructive',
      });
    } finally {
      setNotifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/fees/accounts"
            className="text-blue-500 text-sm inline-flex items-center gap-1 mb-2 hover:text-blue-400"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Accounts
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Fee Dues</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Parent accounts with pending fees. Next due is the latest date from the most recent fee payment; status is Overdue or Due soon.
          </p>
          {usedFallback && !loading && !error && (
            <p className="text-amber-600 dark:text-amber-400 text-xs mt-2">
              Loaded from parent accounts list. Deploy latest backend for dedicated overdue-dues API and Notify.
            </p>
          )}
        </div>

        {canManageFees && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                disabled={notifying || loading || overdueTotal === 0 || notifyUnavailable}
                title={notifyUnavailable ? 'Deploy backend to enable Notify' : undefined}
              >
                {notifying ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Notify
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-background border-border">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-foreground">Send overdue notifications?</AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground">
                  Email and in-app alerts will go to parents for {overdueTotal} overdue account(s) only
                  (Due soon accounts are excluded). Duplicate sends within 30 minutes are skipped.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={handleNotify}
                  disabled={notifying}
                >
                  {notifying ? 'Sending…' : 'Send notifications'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-foreground text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-500" /> Accounts with pending fees
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search name, mobile, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-muted border-border"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <p className="text-red-500 text-center py-8">{error}</p>
          ) : displayRows.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              {meta.total === 0 && !debouncedSearch
                ? 'No pending fee dues. Accounts appear here when a balance remains after payments on a parent account.'
                : 'No accounts match your search.'}
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground">Parent</TableHead>
                    <TableHead className="text-muted-foreground">Mobile</TableHead>
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground text-right">Students</TableHead>
                    <TableHead className="text-muted-foreground text-right">Yearly</TableHead>
                    <TableHead className="text-muted-foreground text-right">Pending</TableHead>
                    <TableHead className="text-muted-foreground">Next due</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayRows.map((row) => (
                    <TableRow
                      key={row.parent_account_id}
                      className="border-border hover:bg-muted/50 cursor-pointer"
                      tabIndex={0}
                      role="button"
                      onClick={() => navigate(`/fees/accounts/${row.parent_account_id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/fees/accounts/${row.parent_account_id}`);
                        }
                      }}
                    >
                      <TableCell>
                        <span className="text-blue-500 font-medium">{row.parent_name || '—'}</span>
                      </TableCell>
                      <TableCell className="text-foreground">{row.parent_mobile || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{row.parent_email || '—'}</TableCell>
                      <TableCell className="text-right text-foreground">{row.student_count ?? 0}</TableCell>
                      <TableCell className="text-right text-foreground">
                        {formatCurrency(row.total_yearly)}
                      </TableCell>
                      <TableCell className="text-right font-medium text-amber-600 dark:text-amber-400">
                        {formatCurrency(row.pending_amount)}
                      </TableCell>
                      <TableCell>
                        {row.next_due_date ? (
                          <span
                            className={
                              row.status === 'Due soon'
                                ? 'text-blue-600 dark:text-blue-400 font-medium'
                                : 'text-amber-600 dark:text-amber-400 font-medium'
                            }
                          >
                            {formatDate(row.next_due_date)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            row.status === 'Due soon'
                              ? 'bg-amber-600/20 text-amber-400 border-amber-600/50'
                              : 'bg-red-600/20 text-red-400 border-red-600/50'
                          }
                        >
                          {row.status || 'Overdue'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!usedFallback && meta.pages > 1 ? (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {meta.page} of {meta.pages} ({meta.total} accounts)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeeAccountDues;
